import { NavLink, useNavigate, useLocation } from 'react-router';
import {
  Bell,
  LogOut,
  Sun,
  Moon,
  ChevronDown,
  Key,
  Wallet,
} from 'lucide-react';
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useAlerts } from '@/hooks/use-alerts';
import { useTheme } from '@/lib/theme';
import { useCurrency, SUPPORTED_CURRENCIES, CURRENCY_META } from '@/lib/currency';
import { Logo } from '@/components/ui/logo';

const navItems = [
  { to: '/dashboard', label: 'Home' },
  { to: '/bots', label: 'Bots' },
  { to: '/strategies', label: 'Strategies' },
  { to: '/copy-trade', label: 'Copy Trading' },
  { to: '/accounts', label: 'Accounts' },
];

function getActiveIndex(pathname: string): number {
  return navItems.findIndex(
    (item) => pathname === item.to || pathname.startsWith(item.to + '/'),
  );
}

export function Topbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { homeCurrency, setHomeCurrency } = useCurrency();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  const { data: alertsData } = useAlerts(
    { includeAcknowledged: false, limit: 1 },
    { refetchInterval: 30_000 },
  );
  const unreadCount = alertsData?.total ?? 0;

  const activeIndex = getActiveIndex(pathname);

  const updateIndicator = useCallback(() => {
    const link = linkRefs.current[activeIndex];
    const nav = navRef.current;
    if (!link || !nav) return;
    const navRect = nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    setIndicator({
      left: linkRect.left - navRect.left,
      width: linkRect.width,
      ready: true,
    });
  }, [activeIndex]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  // Recalculate on resize
  useEffect(() => {
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator]);

  // Close dropdowns on click outside
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  return (
    <header className="flex-shrink-0 flex items-center px-6 lg:px-8 py-3 relative z-20 max-w-7xl mx-auto w-full">
      {/* Logo */}
      <Logo />

      {/* Center nav pill — absolutely positioned so it stays centered regardless of left/right content width */}
      <nav
        ref={navRef}
        className="hidden lg:flex items-center absolute left-1/2 -translate-x-1/2 gap-0.5 glass rounded-full px-1.5 py-1"
      >
        {/* Sliding indicator */}
        {indicator.ready && activeIndex >= 0 && (
          <div
            className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-orange to-amber shadow-glow-sm pointer-events-none"
            style={{
              left: indicator.left,
              width: indicator.width,
              transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        )}
        {navItems.map(({ to, label }, i) => (
          <NavLink
            key={to}
            to={to}
            ref={(el) => { linkRefs.current[i] = el; }}
            className={({ isActive }) =>
              `relative z-10 px-4 py-1.5 rounded-full text-[12px] font-semibold transition-colors duration-200 ${
                isActive ? 'text-white' : 'text-ink-3 hover:text-ink'
              }`
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Right side */}
      <div className="flex items-center gap-3 lg:gap-1.5 ml-auto">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-ink-3 hover:text-orange transition-colors cursor-pointer"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        {/* Alerts bell */}
        <button
          className="relative w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-ink-3 hover:text-ink transition-colors cursor-pointer"
          onClick={() => navigate('/alerts')}
        >
          <Bell size={16} />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-2 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red text-[8px] font-bold text-white flex items-center justify-center leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User chip */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-1.5 lg:pl-1 lg:pr-2.5 lg:py-1 lg:rounded-full lg:glass cursor-pointer hover:shadow-glow-sm transition-shadow"
          >
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange to-amber flex items-center justify-center text-[9px] font-bold text-white uppercase">
              {user?.email?.charAt(0) ?? '?'}
            </div>
            <span className="text-[11px] font-semibold text-ink-2 hidden lg:inline max-w-[100px] truncate">
              {user?.email?.split('@')[0]}
            </span>
            <ChevronDown size={11} className="text-ink-4 hidden lg:block" />
          </button>

          {userMenuOpen && (
            <>
              {/* Backdrop — mobile only */}
              <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setUserMenuOpen(false)} />

              {/* Menu: bottom sheet on mobile, dropdown on desktop */}
              <div className="fixed bottom-0 left-0 right-0 z-50 lg:absolute lg:bottom-auto lg:left-auto lg:right-0 lg:top-full lg:mt-2 lg:w-52 rounded-t-2xl lg:rounded-xl border border-glass-border bg-bg-2 backdrop-blur-xl shadow-modal py-2 safe-area-bottom">
                {/* Drag handle — mobile only */}
                <div className="w-10 h-1 rounded-full bg-ink-4/30 mx-auto mb-2 lg:hidden" />

                {/* User info */}
                <div className="px-4 py-2.5 border-b border-border flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange to-amber flex items-center justify-center text-sm font-bold text-white uppercase flex-shrink-0">
                    {user?.email?.charAt(0) ?? '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-ink truncate">{user?.email?.split('@')[0]}</p>
                    <p className="text-[10px] text-ink-4 truncate">{user?.email}</p>
                  </div>
                </div>

                {/* Currency selector */}
                <div className="px-4 py-2.5 border-b border-border">
                  <p className="text-[9px] text-ink-4 uppercase tracking-wider font-semibold mb-2">Currency</p>
                  <div className="flex flex-wrap gap-1.5">
                    {SUPPORTED_CURRENCIES.map((code) => {
                      const meta = CURRENCY_META[code];
                      return (
                        <button
                          key={code}
                          onClick={() => setHomeCurrency(code)}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer ${
                            code === homeCurrency
                              ? 'text-orange bg-orange/10'
                              : 'text-ink-3 hover:text-ink hover:bg-surface'
                          }`}
                        >
                          <span className="text-sm leading-none">{meta?.flag}</span>
                          {code}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/accounts'); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold text-ink-3 hover:text-ink hover:bg-surface transition-colors cursor-pointer"
                  >
                    <Wallet size={16} />
                    Accounts
                  </button>

                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/settings/api-keys'); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold text-ink-3 hover:text-ink hover:bg-surface transition-colors cursor-pointer"
                  >
                    <Key size={16} />
                    API Keys
                  </button>

                  <div className="border-t border-border my-1" />

                  <button
                    onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold text-ink-3 hover:text-red hover:bg-surface transition-colors cursor-pointer"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
